import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, inject, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatBadgeModule } from '@angular/material/badge';
import { MatLegacyDialogConfig as MatDialogConfig, MatLegacyDialogModule as MatDialogModule } from '@angular/material/legacy-dialog';
import {
    MatLegacyPaginator as MatPaginator,
    MatLegacyPaginatorIntl as MatPaginatorIntl,
    MatLegacyPaginatorModule as MatPaginatorModule,
} from '@angular/material/legacy-paginator';
import { MatLegacyProgressSpinnerModule as MatProgressSpinnerModule } from '@angular/material/legacy-progress-spinner';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, firstValueFrom, map, merge, Observable, Subject, Subscription, switchMap } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, shareReplay, skip, startWith, tap } from 'rxjs/operators';
import { CustomerTypeResolver } from '../../../../dave-data-module/guards/customer-type.resolver';
import { State } from '../../../../dave-data-module/State';
import { getCustomerTypeFetched, getCustomerTypes } from '../../../../dave-data-module/State/selectors/customers.selectors';
import { customerBackendFilter, PaginatedCustomerService } from '../../../../dave-data-module/services/paginated-customer.service';
import { DaveDoubleIconModule } from '../../../../dave-double-icon/dave-double-icon.module';
import { DaveLoadingModule } from '../../../../dave-loading/dave-loading.module';
import { AppButtonModule } from '../../../../dave-utils-module/app-button-module/app-button.module';
import { AppFilterModule } from '../../../../dave-utils-module/app-filter-module/app-filter.module';
import { FilterOption, FILTER_TYPE_MULTI_SELECT } from '../../../../dave-utils-module/app-filter-module/app-filter/app-filter.component';
import { isNotNullOrUndefined, SearchQueriesDebounceTime } from '../../../../helper/helper';
import { CustomerAdministrationMeta } from '../../../../helper/page-metadata';
import { CustomLabelService } from '../../../../services/custom-label/custom-label.service';
import { DefaultFilterService, FilterApps } from '../../../../services/default-filter.service';
import { LoadingService } from '../../../../services/loading.service';
import { GermanPaginatorIntl } from '../../../../services/german-paginator-intl.service';
import { DaveSelectUserModule } from '../../../dave-select-user.module';
import { ISelectedElement, ITableConfig, TableContentType } from '../../multi-select-table/multi-select-table.component';
import { SelectUserDialogBase } from '../select-user-dialog-base';
import { getFetched$ } from '../../../../dave-data-module/helper/helper';
import { CustomerSortProperty, SortDirection } from '@dave/types';
import { Sort } from '@angular/material/sort';
import { CustomerDataService } from '../../../../dave-data-module/services/customer-data.service';

interface ITableData {
    customerId: number;
    selectedForm: FormControl<boolean>;
    specificationTypeId: number;
    name: TableContentType<any> & { __typename: 'string' };
    number: TableContentType<any> & { __typename: 'string' };
}
interface SelectedCustomerType extends ISelectedElement {
    customerId: number;
}
enum filterNames {
    customerType = 'customerType',
}
interface IFilter {
    [filterNames.customerType]: string[];
}
@Component({
    selector: 'app-select-customer',
    templateUrl: './select-customer.component.html',
    styleUrls: ['./select-customer.component.scss'],
    standalone: true,
    providers: [
        PaginatedCustomerService,
        { provide: MatPaginatorIntl, useClass: GermanPaginatorIntl },
    ],
    imports: [
        CommonModule,
        AppButtonModule,
        DaveSelectUserModule,
        DaveDoubleIconModule,
        MatBadgeModule,
        MatTooltipModule,
        FontAwesomeModule,
        AppFilterModule,
        DaveLoadingModule,
        MatPaginatorModule,
        MatProgressSpinnerModule,
    ],
})
export class SelectCustomerComponent<T extends SelectedCustomerType> implements OnInit, OnDestroy, AfterViewInit {
    public static readonly DefaultConfig: MatDialogConfig = SelectUserDialogBase.DefaultConfig;
    @ViewChild(MatPaginator) private paginator?: MatPaginator;

    public FilterSettings$: Observable<FilterOption[]> =
        combineLatest([
            getFetched$(this.store, getCustomerTypeFetched, getCustomerTypes),
            this.cls.getMultiple$('CustomerSpecificationType'),
        ]).pipe(
            map(([customerTypes, customerTypeLabel]) => {
                const ctValues = {};
                customerTypes.forEach((c) => {
                    ctValues[c.Id.toString()] = c.Name;
                });
                return [
                    {
                        Name: filterNames.customerType,
                        Type: FILTER_TYPE_MULTI_SELECT,
                        Label: customerTypeLabel,
                        Values: ctValues,
                    },
                ];
            }),
        );
    public FilterValues$: BehaviorSubject<IFilter> = new BehaviorSubject({
        [filterNames.customerType]: [],
    });
    public FilterAmount$ = this.FilterValues$.pipe(map((v) => (v[filterNames.customerType]?.length ? 1 : 0)));
    public SearchString: BehaviorSubject<string> = new BehaviorSubject<string>('');

    selectedCustomers$ = new BehaviorSubject<T[] | null>(null);
    @Input() set selectedCustomers(value: T[]) {
        this.selectedCustomers$.next(value);
    }
    get selectedCustomers() {
        return this.selectedCustomers$.value;
    }
    @Output() deleteClick = new EventEmitter<T>();
    @Output() entryClick = new EventEmitter<T>();

    TableDataConfig$: Observable<ITableConfig[]> = this.cls.getSingle$('CustomerNumber').pipe(map((customerNumberLabel) => {
        return [
            {
                header: customerNumberLabel,
                id: 'number',
                sortAccessor: (cell: TableContentType<any> & { __typename: 'string' }) => cell.content || '',
            },
            {
                header: 'Name',
                id: 'name',
                sortAccessor: (cell: TableContentType<any> & { __typename: 'string' }) => cell.content || '',
            },
        ]
    }));

    /** Nur die aktuell geladene Seite (serverseitiges Paging via PaginatedCustomerService) */
    TableData$: Observable<ITableData[]>;

    private subscriptions: Subscription[] = [];
    public ContentResolved$: Observable<boolean>;
    public dirty = false;

    /** Gibt ein Mal einen Wert aus, wenn ngAfterViewInit gefeuert wurde (Paginator dann verfügbar) */
    private afterViewInit$ = new BehaviorSubject<boolean>(false);

    /**
     * Als Feld mit inject() statt Konstruktor-Parameter, damit `_customerService` schon für die
     * folgenden Feld-Initializer sicher gesetzt ist - analog zu CustomerListComponent.
     */
    private readonly _customerService = inject(PaginatedCustomerService);
    public PageIsLoading$ = this._customerService.selectIsLoading$;
    public length$ = this._customerService.selectLength$;
    public pageSize$ = this._customerService.selectPageSize$;

    constructor(
        private store: Store<State>,
        public LS: LoadingService,
        private defaultFilter: DefaultFilterService,
        customerTypeRes: CustomerTypeResolver,
        protected cls: CustomLabelService,
        private customerDataService: CustomerDataService,
    ) {
        this.ContentResolved$ = this.store.select(getCustomerTypeFetched).pipe(
            distinctUntilChanged(),
            tap((fetched) => {
                if (!fetched) {
                    customerTypeRes.resolve();
                }
            }),
            shareReplay({ refCount: true, bufferSize: 1 }),
        );

        this.TableData$ = this.afterViewInit$.pipe(
            filter((v) => v),
            tap(() => (this.paginator.pageIndex = 0)),
            switchMap(() =>
                merge(
                    this.paginator.page,
                    this.sortChange$.pipe(
                        tap((sort) => (this.currentSort = sort)),
                        tap(() => (this.paginator.pageIndex = 0)),
                    ),
                    this.FilterValues$.pipe(tap(() => (this.paginator.pageIndex = 0))),
                    this.SearchString.pipe(
                        debounceTime(SearchQueriesDebounceTime),
                        tap(() => (this.paginator.pageIndex = 0)),
                    ),
                ).pipe(
                    startWith(null),
                    switchMap(() => {
                        const page = this.paginator;
                        const offset = page.pageIndex * page.pageSize;
                        this._customerService.loadPage({
                            offset,
                            pageSize: page.pageSize,
                            searchQuery: this.SearchString.value,
                            filterObject: this.toBackendFilter(this.FilterValues$.value),
                            sortProperty: this.getBackendSort(this.currentSort.active, this.currentSort.direction),
                        });
                        return this._customerService.selectItems$;
                    }),
                ),
            ),
            map((customers) =>
                customers.map<ITableData>((c) => ({
                    customerId: c.Id,
                    specificationTypeId: c.CustomerSpecificationTypeId,
                    selectedForm: new FormControl<boolean>(false),
                    name: { __typename: 'string', content: c.DisplayName },
                    number: { __typename: 'string', content: c.CustomerNo },
                })),
            ),
            shareReplay({ refCount: true, bufferSize: 1 }),
        );
    }

    public SelectedCustomers$ = this.selectedCustomers$.pipe(
        filter(isNotNullOrUndefined),
        distinctUntilChanged(
            (a, b) =>
                a.length === b.length &&
                a.every((x, i) => x.customerId === b[i].customerId),
        ),
        switchMap((selectedCustomers) =>
            this.customerDataService
                .GetCustomersDict$(selectedCustomers.map((c) => c.customerId))
                .pipe(
                    map((customers) =>
                        selectedCustomers.map((selectedCustomer) => ({
                            ...selectedCustomer,
                            label: customers[selectedCustomer.customerId]?.DisplayName ?? '',
                        })),
                    ),
                ),
        ),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    public sortChange$ = new Subject<Sort>();
    private currentSort: Sort = { active: 'name', direction: 'asc' };

    private getBackendSort(active: string, direction: string): { property: CustomerSortProperty; direction: SortDirection } {
        let property = CustomerSortProperty.CustomerName;

        switch (active) {
            case 'number':
                property = CustomerSortProperty.CustomerNumber;
                break;
            case 'name':
                property = CustomerSortProperty.CustomerName;
                break;
        }

        return {
            property,
            direction: direction === 'asc' ? SortDirection.asc : SortDirection.desc,
        };
    }

    /** Backend-Filter aus den lokalen FilterValues bauen. */
    private toBackendFilter(filterVal: IFilter): customerBackendFilter {
        return {
            customerSpecificationTypeIds: filterVal[filterNames.customerType]?.length
                ? filterVal[filterNames.customerType].map((id) => Number(id))
                : undefined,

        };
    }

    ngOnInit(): void {
        firstValueFrom(this.defaultFilter.GetFilterByApp$(FilterApps.SelectCommissionPopup)).then((filterValues) => {
            let temp = {
                [filterNames.customerType]: isNotNullOrUndefined(filterValues[filterNames.customerType]) ? filterValues[filterNames.customerType] : [],
            };
            this.FilterValues$.next(temp);
        });

        this.subscriptions.push(
            this.FilterValues$.pipe(skip(2)).subscribe((val) => {
                if (Object.keys(val).length !== 0) {
                    this.defaultFilter.SetFilterByApp(FilterApps.SelectCommissionPopup, val);
                }
            }),
            this.TableData$.pipe(
                switchMap((td) => merge(...td.map((t) => t.selectedForm.valueChanges.pipe(map(() => t.selectedForm.dirty))))),
            ).subscribe((v) => {
                if (v) {
                    this.dirty = true;
                }
            }),
            combineLatest([
                this.TableData$,
                this.SelectedCustomers$,
            ]).subscribe(([data, selectedCustomers]) => {
                data.forEach((d) => {
                    const isSelected = selectedCustomers.some(
                        (u) => u.customerId === d.customerId,
                    );

                    if (isSelected && (d.selectedForm.enabled || !d.selectedForm.value)) {
                        d.selectedForm.setValue(true, { emitEvent: false });
                        d.selectedForm.disable();
                    } else if (!isSelected && (d.selectedForm.disabled || d.selectedForm.value)) {
                        d.selectedForm.setValue(false, { emitEvent: false });
                        d.selectedForm.enable();
                    }
                });
            }),
        );
    }

    ngAfterViewInit(): void {
        this.afterViewInit$.next(true);
    }

    RemoveSelected(entity: T) {
        this.deleteClick.emit(entity);
    }
    clickSelected(entity: T) {
        this.entryClick.emit(entity);
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((s) => s.unsubscribe());
    }
    protected readonly CustomerAdministrationMeta = CustomerAdministrationMeta;
}
